import { Link } from 'react-router-dom';
import { COMPANY, SERVICES, SERVICE_AREAS } from '../../mocks/siteData';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-stone-950">
      {/* Gold accent top line */}
      <div className="h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent" />

      {/* Main Footer */}
      <div className="px-6 lg:px-16 xl:px-24 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">

          {/* Company Info */}
          <div className="lg:col-span-1">
            {/* Logo — white PNG needs dark backing to be visible on dark footer */}
            <div className="inline-block bg-stone-800 rounded-xl px-3 py-2 mb-5">
              <img
                src={COMPANY.logo}
                alt="Bay City Remodeling & Bathrooms LLC"
                className="h-12 w-auto object-contain block"
              />
            </div>
            <p className="text-stone-500 text-xs leading-relaxed mb-5">
              Premium home remodeling in Brandon, FL. Kitchens, bathrooms, and full renovations crafted with precision and built for lasting value.
            </p>
            <div className="flex flex-col gap-2 mb-5">
              <div className="flex items-center gap-2">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-map-pin-line text-accent text-xs" />
                </div>
                <span className="text-stone-500 text-xs">{COMPANY.address}</span>
              </div>
              <a
                href={`tel:${COMPANY.phoneFull}`}
                className="flex items-center gap-2 text-stone-400 hover:text-accent transition-colors"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-phone-line text-accent text-xs" />
                </div>
                <span className="text-sm font-semibold">{COMPANY.phone}</span>
              </a>
            </div>
            {/* Review badge */}
            <div className="inline-flex items-center gap-2.5 bg-stone-900 border border-stone-800 px-4 py-2.5 rounded-xl">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-2.5 h-2.5 flex items-center justify-center">
                    <i className="ri-star-fill text-accent text-[10px]" />
                  </div>
                ))}
              </div>
              <span className="text-white font-semibold text-xs">{COMPANY.rating}</span>
              <span className="text-stone-500 text-xs">({COMPANY.reviewCount} reviews)</span>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-stone-300 font-semibold text-xs uppercase tracking-[0.15em] mb-5">Services</h4>
            <ul className="flex flex-col gap-2">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/services"
                    className="text-stone-500 text-xs hover:text-accent transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="text-stone-300 font-semibold text-xs uppercase tracking-[0.15em] mb-5">Service Areas</h4>
            <ul className="flex flex-col gap-2">
              {SERVICE_AREAS.slice(0, 9).map((area) => (
                <li key={area.slug}>
                  <Link
                    to={`/near-me/${area.slug}`}
                    className="text-stone-500 text-xs hover:text-accent transition-colors"
                  >
                    {area.name}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/near-me"
              className="inline-flex items-center gap-1 text-accent text-xs mt-3 hover:text-accent-light transition-colors"
            >
              View all 23 areas
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs" />
              </div>
            </Link>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-stone-300 font-semibold text-xs uppercase tracking-[0.15em] mb-5">Company</h4>
            <ul className="flex flex-col gap-2 mb-6">
              <li><Link to="/" className="text-stone-500 text-xs hover:text-accent transition-colors">Home</Link></li>
              <li><Link to="/services" className="text-stone-500 text-xs hover:text-accent transition-colors">Services</Link></li>
              <li><Link to="/about" className="text-stone-500 text-xs hover:text-accent transition-colors">About Us</Link></li>
              <li><Link to="/near-me" className="text-stone-500 text-xs hover:text-accent transition-colors">Service Areas</Link></li>
              <li>
                <a
                  href={COMPANY.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-stone-500 text-xs hover:text-accent transition-colors"
                >
                  Google Maps
                </a>
              </li>
              <li>
                <a
                  href={COMPANY.reviewUrl}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-stone-500 text-xs hover:text-accent transition-colors"
                >
                  Leave a Review
                </a>
              </li>
            </ul>
            <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
              <p className="text-stone-500 text-[10px] uppercase tracking-[0.12em] mb-2">Licensed · Insured · Brandon, FL</p>
              <a
                href={`tel:${COMPANY.phoneFull}`}
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-dark text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap"
              >
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-phone-fill text-xs" />
                </div>
                Call for Free Quote
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-900 px-6 lg:px-16 xl:px-24 py-5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-stone-600 text-[11px] text-center sm:text-left">
            © {currentYear} <strong className="text-stone-500">Bay City Remodeling &amp; Bathrooms LLC</strong>. All rights reserved.
          </p>
          <p className="text-stone-700 text-[11px]">Brandon, FL 33511 &nbsp;·&nbsp; Home Remodeling Specialists</p>
        </div>
      </div>
    </footer>
  );
}
